import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

/**
 * Public-safe shape of `/api/v1/tenants/by-slug/{slug}`. Matches
 * the backend's `TenantPublicResponse` DTO — keep these in sync.
 */
export type TenantPublic = {
  id: string;
  slug: string;
  name: string;
};

/**
 * Resolve a tenant's UUID from its environment-stable slug. Replaces
 * the legacy `VITE_DEFAULT_TENANT_ID` build-time env var which went
 * stale on every fresh DB (Flyway uses `gen_random_uuid()`).
 *
 * <p>Cached forever (`staleTime: Infinity`) — the tenant id for a
 * given slug never changes within a session. TanStack Query keys by
 * `['tenant', slug]` so multiple callers (CustomerLayout, CreditsPage,
 * etc.) share the same single fetch.
 */
export function useTenantBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ['tenant', slug],
    queryFn: () => apiClient<TenantPublic>(`/tenants/by-slug/${slug}`, { skipAuth: true }),
    enabled: !!slug,
    staleTime: Infinity,
  });
}
